import api from '../api';
import { unwrapApiResponse } from '@/utils/api-response.util';
import {
  AssignedClient,
  ConstructionTemplate,
  ConstructionTemplateDetail,
  CreateOptionDto,
  CreateQuestionDto,
  CreateStepDto,
  CreateTemplateDto,
  QuestionType,
  UpdateOptionDto,
  UpdateQuestionDto,
  UpdateStepDto,
  UpdateTemplateDto,
} from '@/types/questionnaire-construction';

/**
 * Однакова логіка для двох бекенд-шляхів: `/expert/questionnaires-construction` (свої+системні,
 * з перевіркою власності) і `/admin/questionnaires-construction` (повний доступ до всіх шаблонів).
 * Бекенд розділяє їх окремими контролерами/ролями — тут лише різний базовий шлях.
 */
export function createQuestionnairesConstructionService(baseUrl: string) {
  return {
    async getOptions(): Promise<{ questionTypes: QuestionType[] }> {
      const response = await api.get(`${baseUrl}/options`);
      return unwrapApiResponse(response.data);
    },

    async getTemplates(): Promise<ConstructionTemplate[]> {
      const response = await api.get(`${baseUrl}/templates`);
      return unwrapApiResponse(response.data);
    },

    async getTemplate(id: number): Promise<ConstructionTemplateDetail> {
      const response = await api.get(`${baseUrl}/templates/${id}`);
      return unwrapApiResponse(response.data);
    },

    async createTemplate(dto: CreateTemplateDto): Promise<ConstructionTemplate> {
      const response = await api.post(`${baseUrl}/templates`, dto);
      return unwrapApiResponse(response.data);
    },

    async updateTemplate(id: number, dto: UpdateTemplateDto): Promise<ConstructionTemplate> {
      const response = await api.patch(`${baseUrl}/templates/${id}`, dto);
      return unwrapApiResponse(response.data);
    },

    async deleteTemplate(id: number): Promise<{ softDeleted: boolean }> {
      const response = await api.delete(`${baseUrl}/templates/${id}`);
      return unwrapApiResponse(response.data);
    },

    async createStep(templateId: number, dto: CreateStepDto): Promise<{ id: number }> {
      const response = await api.post(`${baseUrl}/templates/${templateId}/steps`, dto);
      return unwrapApiResponse(response.data);
    },

    async updateStep(stepId: number, dto: UpdateStepDto): Promise<void> {
      await api.patch(`${baseUrl}/steps/${stepId}`, dto);
    },

    async deleteStep(stepId: number): Promise<void> {
      await api.delete(`${baseUrl}/steps/${stepId}`);
    },

    async createQuestion(stepId: number, dto: CreateQuestionDto): Promise<{ id: number }> {
      const response = await api.post(`${baseUrl}/steps/${stepId}/questions`, dto);
      return unwrapApiResponse(response.data);
    },

    async updateQuestion(questionId: number, dto: UpdateQuestionDto): Promise<void> {
      await api.patch(`${baseUrl}/questions/${questionId}`, dto);
    },

    async deleteQuestion(questionId: number): Promise<void> {
      await api.delete(`${baseUrl}/questions/${questionId}`);
    },

    async createOption(questionId: number, dto: CreateOptionDto): Promise<{ id: number }> {
      const response = await api.post(`${baseUrl}/questions/${questionId}/options`, dto);
      return unwrapApiResponse(response.data);
    },

    async updateOption(optionId: number, dto: UpdateOptionDto): Promise<void> {
      await api.patch(`${baseUrl}/options/${optionId}`, dto);
    },

    async deleteOption(optionId: number): Promise<void> {
      await api.delete(`${baseUrl}/options/${optionId}`);
    },

    /**
     * Створює редаговану копію шаблону. Послідовно відтворює усі кроки → питання → варіанти
     * через ті самі ендпоінти, якими користується конструктор.
     */
    async cloneTemplate(id: number): Promise<ConstructionTemplate> {
      const source = await this.getTemplate(id);

      const created = await this.createTemplate({
        nestNameI18n: { ...(source.nestNameI18n ?? {}), uk: `${source.nestNameI18n?.uk ?? 'Опитувальник'} (копія)` },
        nestDescriptionI18n: source.nestDescriptionI18n,
        nestWelcomeNotesI18n: source.nestWelcomeNotesI18n,
      });

      for (const step of source.steps) {
        const newStep = await this.createStep(created.id, {
          order: step.order,
          nestCaptionI18n: step.nestCaptionI18n,
          nestDescriptionI18n: step.nestDescriptionI18n,
        });

        for (const question of step.questions) {
          const newQuestion = await this.createQuestion(newStep.id, {
            typeId: question.typeId,
            name: question.name,
            order: question.order,
            isRequired: !!question.isRequired,
            nestCaptionI18n: question.nestCaptionI18n,
            nestTooltipI18n: question.nestTooltipI18n,
            nestNoteI18n: question.nestNoteI18n,
            nestPlaceholderI18n: question.nestPlaceholderI18n,
            min: question.min ?? undefined,
            max: question.max ?? undefined,
            relatedAction: question.relatedAction ?? undefined,
            dependency: question.dependency ?? undefined,
            pattern: question.pattern ?? undefined,
          });

          for (const option of question.options) {
            await this.createOption(newQuestion.id, {
              value: option.value,
              order: option.order,
              isDefault: !!option.isDefault,
              caption: option.caption ?? undefined,
              nestCaptionI18n: option.nestCaptionI18n,
              noteI18n: option.noteI18n,
            });
          }
        }
      }

      return created;
    },

    async getInviteLink(id: number): Promise<{ token: string }> {
      const response = await api.post(`${baseUrl}/templates/${id}/invite-link`);
      return unwrapApiResponse(response.data);
    },

    async assignTemplate(id: number, clientIds: number[]): Promise<{ assigned: number[]; alreadyAssigned: number[]; rejected: number[] }> {
      const response = await api.post(`${baseUrl}/templates/${id}/assign`, { clientIds });
      return unwrapApiResponse(response.data);
    },

    async getAssignedClients(id: number): Promise<AssignedClient[]> {
      const response = await api.get(`${baseUrl}/templates/${id}/assigned-clients`);
      return unwrapApiResponse(response.data);
    },

    async downloadBlankFormPdf(id: number, templateName: string): Promise<void> {
      const response = await api.post(`${baseUrl}/templates/${id}/pdf`, {}, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${templateName || 'questionnaire'}-blank.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    },
  };
}

export const questionnairesConstructionService = createQuestionnairesConstructionService('/expert/questionnaires-construction');
export const adminQuestionnairesConstructionService = createQuestionnairesConstructionService('/admin/questionnaires-construction');

export type QuestionnairesConstructionService = ReturnType<typeof createQuestionnairesConstructionService>;
